import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';

const Prompts: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Mock data for prompts
  const promptsData = {
    totalPrompts: 156,
    activePrompts: 89,
    performanceScore: 87,
    categories: [
      { id: 'all', name: 'All Prompts', count: 156 },
      { id: 'seo', name: 'SEO Optimization', count: 45 },
      { id: 'content', name: 'Content Generation', count: 38 },
      { id: 'social', name: 'Social Media', count: 32 },
      { id: 'ads', name: 'Ad Copy', count: 28 },
      { id: 'email', name: 'Email Marketing', count: 13 }
    ],
    prompts: [
      {
        id: 1,
        title: 'SEO Meta Description Generator',
        category: 'seo',
        description: 'Generate compelling meta descriptions for better search engine visibility',
        performance: 94,
        usage: 234,
        lastUsed: '2025-08-12',
        status: 'active'
      },
      {
        id: 2,
        title: 'Blog Post Outline Creator',
        category: 'content',
        description: 'Create structured blog post outlines based on keywords and topics',
        performance: 89,
        usage: 187,
        lastUsed: '2025-08-11',
        status: 'active'
      },
      {
        id: 3,
        title: 'Social Media Caption Writer',
        category: 'social',
        description: 'Generate engaging social media captions for different platforms',
        performance: 92,
        usage: 156,
        lastUsed: '2025-08-10',
        status: 'active'
      },
      {
        id: 4,
        title: 'Google Ads Headline Generator',
        category: 'ads',
        description: 'Create high-converting Google Ads headlines with character limits',
        performance: 87,
        usage: 98,
        lastUsed: '2025-08-09',
        status: 'active'
      },
      {
        id: 5,
        title: 'Email Subject Line Optimizer',
        category: 'email',
        description: 'Generate compelling email subject lines to improve open rates',
        performance: 91,
        usage: 67,
        lastUsed: '2025-08-08',
        status: 'active'
      },
      {
        id: 6,
        title: 'Product Description Writer',
        category: 'content',
        description: 'Create detailed and persuasive product descriptions',
        performance: 85,
        usage: 145,
        lastUsed: '2025-08-07',
        status: 'active'
      }
    ],
    recentActivity: [
      {
        id: 1,
        prompt: 'SEO Meta Description Generator',
        action: 'used',
        user: 'John Doe',
        time: '2 hours ago',
        result: 'Generated 5 meta descriptions'
      },
      {
        id: 2,
        prompt: 'Blog Post Outline Creator',
        action: 'modified',
        user: 'Jane Smith',
        time: '4 hours ago',
        result: 'Updated prompt parameters'
      },
      {
        id: 3,
        prompt: 'Social Media Caption Writer',
        action: 'used',
        user: 'Mike Johnson',
        time: '6 hours ago',
        result: 'Generated 3 captions'
      }
    ]
  };

  const filteredPrompts = selectedCategory === 'all' 
    ? promptsData.prompts 
    : promptsData.prompts.filter(prompt => prompt.category === selectedCategory);

  const getPerformanceColor = (score: number) => {
    if (score >= 90) return 'text-green-600 bg-green-100';
    if (score >= 80) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getStatusColor = (status: string) => {
    return status === 'active' ? 'text-green-600 bg-green-100' : 'text-gray-600 bg-gray-100';
  };

  return (
    <>
      <Helmet>
        <title>Prompts - Dream Digi Platform</title>
      </Helmet>
      
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">AI Prompts</h1>
          <p className="text-gray-600">Manage and optimize your AI prompts for better content generation</p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Prompts</p>
                <p className="text-2xl font-bold text-gray-900">{promptsData.totalPrompts}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <i className="fas fa-lightbulb text-blue-600 text-xl"></i>
              </div>
            </div>
            <div className="mt-4">
              <span className="text-green-600 text-sm font-medium">+12</span>
              <span className="text-gray-500 text-sm ml-1">this month</span>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Prompts</p>
                <p className="text-2xl font-bold text-gray-900">{promptsData.activePrompts}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <i className="fas fa-check-circle text-green-600 text-xl"></i>
              </div>
            </div>
            <div className="mt-4">
              <span className="text-green-600 text-sm font-medium">+8</span>
              <span className="text-gray-500 text-sm ml-1">this month</span>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Performance Score</p>
                <p className="text-2xl font-bold text-gray-900">{promptsData.performanceScore}%</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <i className="fas fa-chart-line text-purple-600 text-xl"></i>
              </div>
            </div>
            <div className="mt-4">
              <span className="text-green-600 text-sm font-medium">+5%</span>
              <span className="text-gray-500 text-sm ml-1">from last month</span>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Usage</p>
                <p className="text-2xl font-bold text-gray-900">1,247</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <i className="fas fa-play text-orange-600 text-xl"></i>
              </div>
            </div>
            <div className="mt-4">
              <span className="text-green-600 text-sm font-medium">+23%</span>
              <span className="text-gray-500 text-sm ml-1">from last month</span>
            </div>
          </div>
        </div>

        {/* Categories Filter */}
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Categories</h3>
          <div className="flex flex-wrap gap-3">
            {promptsData.categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === category.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category.name} ({category.count})
              </button>
            ))}
          </div>
        </div>

        {/* Prompts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {filteredPrompts.map((prompt) => (
            <div key={prompt.id} className="bg-white rounded-lg shadow p-6 border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">{prompt.title}</h3>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(prompt.status)}`}>
                  {prompt.status.charAt(0).toUpperCase() + prompt.status.slice(1)}
                </span>
              </div>
              
              <p className="text-sm text-gray-600 mb-4">{prompt.description}</p>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Performance</span>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPerformanceColor(prompt.performance)}`}>
                    {prompt.performance}%
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Usage</span>
                  <span className="text-sm font-medium text-gray-900">{prompt.usage} times</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Last Used</span>
                  <span className="text-sm text-gray-900">{prompt.lastUsed}</span>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex space-x-2">
                  <button className="flex-1 bg-blue-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-blue-700 transition-colors">
                    Use Prompt
                  </button>
                  <button className="px-3 py-2 border border-gray-300 rounded text-sm font-medium hover:bg-gray-50 transition-colors">
                    <i className="fas fa-edit"></i>
                  </button>
                  <button className="px-3 py-2 border border-gray-300 rounded text-sm font-medium hover:bg-gray-50 transition-colors">
                    <i className="fas fa-copy"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-4">
            {promptsData.recentActivity.map((activity) => (
              <div key={activity.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-full">
                    <i className="fas fa-play text-blue-600 text-sm"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{activity.prompt}</p>
                    <p className="text-xs text-gray-500">
                      {activity.action} by {activity.user} • {activity.time}
                    </p>
                  </div>
                </div>
                <span className="text-sm text-gray-600">{activity.result}</span>
              </div>
            ))}
          </div>
        </div>

        {/* AI Insights */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 border border-blue-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <i className="fas fa-robot text-blue-600 mr-2"></i>
            AI Prompt Insights
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg p-4 border border-blue-200">
              <h4 className="font-medium text-gray-900 mb-2">Performance Optimization</h4>
              <p className="text-sm text-gray-600">Your SEO prompts are performing exceptionally well. Consider creating more specialized variations.</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-blue-200">
              <h4 className="font-medium text-gray-900 mb-2">Usage Patterns</h4>
              <p className="text-sm text-gray-600">Content generation prompts are most popular. Focus on expanding this category.</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-blue-200">
              <h4 className="font-medium text-gray-900 mb-2">Improvement Opportunities</h4>
              <p className="text-sm text-gray-600">Email marketing prompts have lower usage. Consider optimizing for better engagement.</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-blue-200">
              <h4 className="font-medium text-gray-900 mb-2">New Prompt Suggestions</h4>
              <p className="text-sm text-gray-600">Based on usage patterns, consider adding video script and podcast outline prompts.</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Prompts;
